/**
 * @file Compare two page versions: pick versions, side-by-side rendered preview, optional HTML diff, rollback.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestoreIcon from "@mui/icons-material/Restore";
import { apiFetch } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export interface VersionCompareSnapshot {
  id: string;
  title: string;
  body: string;
  versionNumber: number;
}

export interface VersionCompareDiffChunk {
  value: string;
  count?: number;
  added?: boolean;
  removed?: boolean;
}

export interface VersionCompareData {
  version1: VersionCompareSnapshot;
  version2: VersionCompareSnapshot;
  diff: VersionCompareDiffChunk[];
}

export interface VersionListEntry {
  id: string;
  versionNumber: number;
  title: string;
  source: string;
  createdAt: string;
  /** Username from the access token when this version was saved. */
  createdBy: string;
}

const PREVIEW_BODY_CLASS =
  "preview-content [&_a]:text-sky-700 [&_a]:underline [&_iframe]:aspect-video [&_iframe]:w-full [&_img]:max-w-full [&_video]:max-w-full [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6";

type Props = {
  open: boolean;
  pageId: string;
  /** UUIDs of the two versions to compare when the dialog opens. */
  seedV1: string;
  seedV2: string;
  versions: VersionListEntry[];
  canEdit: boolean;
  onClose: () => void;
  /** Called after a successful rollback (reload page + versions). */
  onRollbackSuccess: () => void | Promise<void>;
  onFetchError?: (message: string) => void;
};

export function VersionCompareDialog({
  open,
  pageId,
  seedV1,
  seedV2,
  versions,
  canEdit,
  onClose,
  onRollbackSuccess,
  onFetchError,
}: Props) {
  const [v1Id, setV1Id] = useState("");
  const [v2Id, setV2Id] = useState("");
  const [data, setData] = useState<VersionCompareData | null>(null);
  const [loading, setLoading] = useState(false);
  const [pairError, setPairError] = useState<string | null>(null);
  const [rollbackId, setRollbackId] = useState<string | null>(null);
  const [rollbackLabel, setRollbackLabel] = useState("");

  useEffect(() => {
    if (open) {
      setV1Id(seedV1);
      setV2Id(seedV2);
    } else {
      setData(null);
      setPairError(null);
    }
  }, [open, seedV1, seedV2]);

  const fetchCompare = useCallback(
    async (a: string, b: string) => {
      if (!a || !b) {
        setData(null);
        setPairError(null);
        return;
      }
      if (a === b) {
        setData(null);
        setPairError(null);
        return;
      }
      setLoading(true);
      setPairError(null);
      try {
        const cmp = await apiFetch<VersionCompareData>(
          `/api/v1/pages/${pageId}/versions/compare?v1=${encodeURIComponent(a)}&v2=${encodeURIComponent(b)}`
        );
        setData(cmp);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setData(null);
        setPairError(msg);
        onFetchError?.(msg);
      } finally {
        setLoading(false);
      }
    },
    [pageId, onFetchError]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    void fetchCompare(v1Id, v2Id);
  }, [open, v1Id, v2Id, fetchCompare]);

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.versionNumber - a.versionNumber),
    [versions]
  );

  const sameVersionPair = Boolean(v1Id && v2Id && v1Id === v2Id);

  const { left, right } = useMemo(() => {
    if (!data) {
      return { left: null as VersionCompareSnapshot | null, right: null as VersionCompareSnapshot | null };
    }
    const a = data.version1;
    const b = data.version2;
    if (a.versionNumber <= b.versionNumber) {
      return { left: a, right: b };
    }
    return { left: b, right: a };
  }, [data]);

  const versionMetaById = useMemo(() => {
    const m = new Map<string, { createdAt: string; createdBy: string }>();
    for (const v of versions) {
      m.set(v.id, { createdAt: v.createdAt, createdBy: v.createdBy });
    }
    return m;
  }, [versions]);

  function formatVersionWhen(iso: string | undefined) {
    if (!iso) {
      return null;
    }
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }

  function renderPreviewHeader(snapshot: VersionCompareSnapshot) {
    const meta = versionMetaById.get(snapshot.id);
    const when = meta ? formatVersionWhen(meta.createdAt) : null;
    const by = meta?.createdBy?.trim() || "—";
    const datePart = when ?? "—";
    const metaLine = `v${snapshot.versionNumber} ( ${datePart} by ${by} )`;
    const titleLine = snapshot.title?.trim() || "Untitled";

    return (
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          bgcolor: "grey.100",
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            noWrap
            title={metaLine}
            sx={{ fontWeight: 600, lineHeight: 1.35 }}
          >
            {metaLine}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            noWrap
            title={titleLine}
            sx={{ lineHeight: 1.35, opacity: 0.92 }}
          >
            {titleLine}
          </Typography>
        </Box>
        {canEdit ? (
          <Tooltip title="Restore">
            <IconButton
              size="small"
              color="warning"
              aria-label={`Restore version v${snapshot.versionNumber}`}
              onClick={() => {
                setRollbackId(snapshot.id);
                setRollbackLabel(`v${snapshot.versionNumber}`);
              }}
              sx={{ flexShrink: 0 }}
            >
              <RestoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    );
  }

  function swapSides() {
    setV1Id(v2Id);
    setV2Id(v1Id);
  }

  async function runRollback(versionId: string) {
    await apiFetch(`/api/v1/pages/${pageId}/rollback/${versionId}`, { method: "POST" });
    await onRollbackSuccess();
    onClose();
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        slotProps={{
          paper: {
            sx: {
              height: { xs: "100%", sm: "85vh" },
              maxHeight: { xs: "100%", sm: "85vh" },
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          Compare versions
          <Tooltip title="Close">
            <IconButton
              edge="end"
              color="inherit"
              aria-label="Close compare dialog"
              onClick={onClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            pt: 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2, mb: 2, flexShrink: 0 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="cmp-v1-label">First version</InputLabel>
              <Select
                labelId="cmp-v1-label"
                label="First version"
                value={v1Id}
                onChange={(e) => setV1Id(e.target.value)}
              >
                {sortedVersions.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    v{v.versionNumber} — {v.title || "Untitled"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Swap versions">
              <IconButton onClick={swapSides} aria-label="Swap versions" size="small" sx={{ mb: 0.5 }}>
                <SwapHorizIcon />
              </IconButton>
            </Tooltip>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="cmp-v2-label">Second version</InputLabel>
              <Select
                labelId="cmp-v2-label"
                label="Second version"
                value={v2Id}
                onChange={(e) => setV2Id(e.target.value)}
              >
                {sortedVersions.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    v{v.versionNumber} — {v.title || "Untitled"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {sameVersionPair ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 240,
                  px: 2,
                }}
              >
                <Typography variant="body1" color="text.secondary" align="center">
                  Choose two different versions to compare.
                </Typography>
              </Box>
            ) : loading ? (
              <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", py: 4 }}>
                <CircularProgress size={36} />
              </Box>
            ) : null}

            {!sameVersionPair && pairError ? (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {pairError}
              </Typography>
            ) : null}

            {!sameVersionPair && !loading && data && left && right ? (
            <>
              {left.title !== right.title ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Title
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    v{left.versionNumber}: {left.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    v{right.versionNumber}: {right.title}
                  </Typography>
                </Box>
              ) : null}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview (how each version looks)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Older content is on the left, newer on the right when version numbers differ.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    overflow: "hidden",
                  }}
                >
                  {renderPreviewHeader(left)}
                  <Box
                    className={PREVIEW_BODY_CLASS}
                    sx={{ p: 2, maxHeight: 360, overflow: "auto", fontSize: "0.9rem" }}
                    dangerouslySetInnerHTML={{ __html: left.body }}
                  />
                </Box>
                <Box
                  sx={{
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    overflow: "hidden",
                  }}
                >
                  {renderPreviewHeader(right)}
                  <Box
                    className={PREVIEW_BODY_CLASS}
                    sx={{ p: 2, maxHeight: 360, overflow: "auto", fontSize: "0.9rem" }}
                    dangerouslySetInnerHTML={{ __html: right.body }}
                  />
                </Box>
              </Box>

              <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
                  <Typography variant="subtitle2">HTML source changes (advanced)</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Technical line diff on stored HTML. Red = only in the first selected version; green = only in the
                    second.
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.06),
                      maxHeight: 280,
                      overflow: "auto",
                      p: 1,
                    }}
                  >
                    {data.diff.map((part, index) => {
                      const isAdd = Boolean(part.added);
                      const isRemove = Boolean(part.removed);
                      return (
                        <Box
                          key={index}
                          component="pre"
                          sx={{
                            m: 0,
                            py: 0.25,
                            px: isAdd || isRemove ? 1 : 0.5,
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            fontSize: "0.75rem",
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            borderLeft: 3,
                            borderLeftStyle: "solid",
                            borderLeftColor: isAdd ? "success.main" : isRemove ? "error.main" : "transparent",
                            bgcolor: (theme) =>
                              isAdd
                                ? alpha(theme.palette.success.main, 0.14)
                                : isRemove
                                  ? alpha(theme.palette.error.main, 0.1)
                                  : "transparent",
                          }}
                        >
                          {part.value}
                        </Box>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end", flexShrink: 0 }}>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={rollbackId !== null}
        title="Restore version"
        message={`Create a new version from snapshot ${rollbackLabel}? Your current draft will remain until you save over it.`}
        confirmLabel="Restore"
        cancelLabel="Cancel"
        danger
        onConfirm={() => {
          const id = rollbackId;
          setRollbackId(null);
          if (id) {
            void runRollback(id).catch((e) => onFetchError?.(e instanceof Error ? e.message : String(e)));
          }
        }}
        onClose={() => setRollbackId(null)}
      />
    </>
  );
}
